/**
 * CSS injection utilities for both shadow DOM and document head contexts
 *
 * This module handles CSS injection for:
 * - Shadow DOM contexts using Vite's ?raw imports (SustainabilityBadge.css)
 * - Document head contexts using <link> elements (ComponentUI.css, etc.)
 *
 * It implements a hybrid approach where:
 * - SustainabilityBadge.css is injected into shadow DOM
 * - ComponentUI.css and other stylesheets are loaded via document head
 */

// Import CSS files as raw text for shadow DOM usage (build-time bundling)
import sustainabilityBadgeCss from "../../../../assets/styles/sustainability/SustainabilityBadge.css?raw";

/**
 * Inject CSS into shadow DOM using a style element
 *
 * @param shadowRoot - The shadow root to inject CSS into
 */
export function injectShadowDOMCSS(shadowRoot: ShadowRoot): void {
  const style = document.createElement("style");
  style.textContent = `
    ${sustainabilityBadgeCss}
  `;
  shadowRoot.appendChild(style);
}

export function isStylesheetLoaded(identifier: string): boolean {
  return !!document.head.querySelector(
    `link[data-sustainability-styles="${identifier}"]`,
  );
}

export function createLinkElement(
  href: string,
  identifier: string,
): HTMLLinkElement {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-sustainability-styles", identifier);
  return link;
}

export function loadDocumentStylesheet(
  url: string,
  identifier: string,
): (() => void) | null {
  // Check if stylesheet is already loaded
  if (isStylesheetLoaded(identifier)) {
    return null;
  }

  const link = createLinkElement(url, identifier);
  document.head.appendChild(link);

  return () => {
    if (document.head.contains(link)) {
      document.head.removeChild(link);
    }
  };
}
